// src/types/notifications.types.ts
// Étendu — compatible avec l'existant

export type NotificationPriority = 'low' | 'normal' | 'high' | 'urgent';
export type NotificationStatus = 'unread' | 'read' | 'archived';

export type TargetRole =
  | 'admin' | 'staff' | 'seller' | 'broker' | 'byod'
  | 'buyer' | 'lender' | 'consultant' | 'partner' | 'expert';

export type NotificationType =
  // Anciens types conservés
  | 'deal_event' | 'document' | 'message' | 'expert' | 'lender'
  | 'pre_eval' | 'cim' | 'nda' | 'loi' | 'meeting' | 'system'
  // Pipeline 12 étapes
  | 'deal_submitted' | 'deal_pre_evaluated' | 'nda_generated' | 'nda_signed'
  | 'cim_generated' | 'cim_approved_internal' | 'cim_approved_seller'
  | 'financing_offer' | 'loi_submitted' | 'offer_accepted'
  | 'due_diligence' | 'closing'
  // Actions parallèles
  | 'message_received' | 'contact_request' | 'booking_confirmed'
  | 'report_purchased' | 'document_uploaded' | 'deal_status_changed'
  // Alertes délai staff/admin
  | 'message_unread_24h' | 'message_unread_48h'
  | 'deal_unreviewed_48h' | 'deal_unreviewed_72h';

export interface UserNotification {
  id: string;
  userId: string;
  targetRoles?: TargetRole[];
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  relatedDealId?: string;
  relatedUserId?: string;
  relatedDocumentId?: string;
  relatedConversationId?: string;
  actionUrl?: string;
  seen: boolean;
  seenAt?: Date;
  status?: NotificationStatus;
  archivedAt?: Date;
  emailSent: boolean;
  smsSent?: boolean;
  createdAt: Date;
  expiresAt?: Date;
  metadata?: Record<string, unknown>;
}

export interface NotificationPreferences {
  userId: string;
  emailEnabled: boolean;
  smsEnabled: boolean;
  pushEnabled: boolean;
  dealEvents: boolean;
  documents: boolean;
  messages: boolean;
  experts: boolean;
  lenders: boolean;
  meetings: boolean;
  quietHoursEnabled: boolean;
  quietHoursStart?: string;
  quietHoursEnd?: string;
}

export interface CreateNotificationRequest {
  userId: string;
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message: string;
  relatedDealId?: string;
  relatedUserId?: string;
  relatedDocumentId?: string;
  actionUrl?: string;
  sendEmail?: boolean;
  sendSms?: boolean;
  metadata?: Record<string, unknown>;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: Omit<NotificationPreferences, 'userId'> = {
  emailEnabled: true,
  smsEnabled: false,
  pushEnabled: true,
  dealEvents: true,
  documents: true,
  messages: true,
  experts: true,
  lenders: true,
  meetings: true,
  quietHoursEnabled: false,
};

export const NOTIFICATION_PRIORITY_MAP: Partial<Record<NotificationType, NotificationPriority>> = {
  deal_submitted: 'high', deal_pre_evaluated: 'high', nda_generated: 'normal',
  nda_signed: 'high', cim_generated: 'high', cim_approved_internal: 'high',
  cim_approved_seller: 'high', financing_offer: 'high', loi_submitted: 'urgent',
  offer_accepted: 'urgent', due_diligence: 'high', closing: 'urgent',
  message_received: 'normal', contact_request: 'high', booking_confirmed: 'normal',
  report_purchased: 'normal', document_uploaded: 'normal', deal_status_changed: 'normal',
  message_unread_24h: 'normal', message_unread_48h: 'high',
  deal_unreviewed_48h: 'normal', deal_unreviewed_72h: 'high',
};

export const NOTIFICATION_TARGET_ROLES: Partial<Record<NotificationType, TargetRole[]>> = {
  deal_submitted: ['staff', 'admin'],
  deal_pre_evaluated: ['seller', 'broker', 'byod'],
  nda_generated: ['buyer'],
  nda_signed: ['staff', 'seller', 'broker', 'buyer'],
  cim_generated: ['staff'],
  cim_approved_internal: ['seller', 'broker'],
  cim_approved_seller: ['staff', 'buyer', 'lender'],
  financing_offer: ['buyer', 'byod', 'staff', 'seller', 'broker'],
  loi_submitted: ['seller', 'broker', 'staff', 'buyer'],
  offer_accepted: ['seller', 'broker', 'buyer', 'staff', 'admin'],
  due_diligence: ['expert', 'partner'],
  closing: ['seller', 'broker', 'buyer', 'staff', 'admin'],
  message_received: ['seller', 'broker', 'buyer', 'staff', 'admin', 'consultant', 'lender', 'partner', 'expert'],
  contact_request: ['seller', 'broker', 'staff', 'consultant'],
  booking_confirmed: ['seller', 'broker', 'buyer', 'staff', 'admin', 'consultant'],
  report_purchased: ['staff', 'buyer', 'byod'],
  document_uploaded: ['staff'],
  deal_status_changed: ['seller', 'broker', 'buyer'],
  deal_event: ['staff', 'admin'],
  system: ['admin'],
  message_unread_24h: ['staff', 'admin'],
  message_unread_48h: ['staff', 'admin'],
  deal_unreviewed_48h: ['staff', 'admin'],
  deal_unreviewed_72h: ['staff', 'admin'],
};

export const NOTIFICATION_URGENCY_COLOR: Partial<Record<NotificationType, string>> = {
  message_unread_24h: '#F59E0B', message_unread_48h: '#EF4444',
  deal_unreviewed_48h: '#F59E0B', deal_unreviewed_72h: '#EF4444',
  loi_submitted: '#EF4444', offer_accepted: '#EF4444', closing: '#EF4444',
  deal_submitted: '#3B82F6', cim_generated: '#3B82F6', nda_signed: '#3B82F6',
  financing_offer: '#3B82F6', contact_request: '#3B82F6',
  cim_approved_seller: '#10B981', due_diligence: '#6366F1',
  booking_confirmed: '#10B981', report_purchased: '#10B981', message_received: '#3B82F6',
};

export interface CreateNotificationPayload {
  type: NotificationType;
  title: string;
  message: string;
  targetUserIds?: string[];
  targetRoles?: TargetRole[];
  relatedDealId?: string;
  relatedUserId?: string;
  relatedDocumentId?: string;
  relatedConversationId?: string;
  actionUrl?: string;
  metadata?: Record<string, unknown>;
  sendEmail?: boolean;
  sendSms?: boolean;
}